import imgix from "@lonelyplanet/dotcom-core/dist/helpers/imgix";
import React from "react";
import PropTypes from "prop-types";
import radium from "radium";
import upperFirst from "lodash/upperFirst";
import isEmpty from "lodash/isEmpty";
import Heading from "backpack-ui/dist/components/text/textHeading";
import ReviewedBadge from "backpack-ui/dist/components/reviewedBadge";
import TextUppercase from "backpack-ui/dist/components/text/textUppercase";
import TextBodySmall from "backpack-ui/dist/components/text/textBodySmall";
import font from "backpack-ui/dist/utils/font";
import colors from "backpack-ui/dist/styles/colors";
import mq from "backpack-ui/dist/styles/mq";
import {
  fontSizeBodySmall,
  fontSizeHeading3,
  fontSizeHeading5,
  fontSizeHeading6,
  fontSizeHeading7,
  fontSizeUppercase,
  fontWeightLight,
  fontWeightMedium,
  lineHeightBodySmall,
  lineHeightHeading3,
  lineHeightHeading5,
  lineHeightHeading6,
  lineHeightHeading7,
  lineHeightReset,
} from "backpack-ui/dist/styles/typography";
import ListItemHotelCallToAction from "./callToAction";
import Bullet from "../decoration/bullet";
import { distanceTo, distanceUnits } from "../../utils/distanceTo";
import ListItemImage from "../../../shared/components/listItemImage";
import hotelProvider from "../../../shared/hotelProvider";
import { ratingCopy } from "../../../shared/lodgingRating";
import currencySymbol from "../../../shared/utils/currencySymbol";

const _ = { upperFirst };

const styles = {
  anchor: {
    display: "block",
  },

  container: {
    alignItems: "center",
    borderTop: `1px solid ${colors.borderPrimary}`,
    display: "flex",
    paddingBottom: "16px",
    paddingTop: "16px",

    [`@media (min-width: ${mq.min["768"]})`]: {
      paddingBottom: "32px",
      paddingTop: "32px",
    },
  },

  imageContainer: {
    flex: "0 0 auto",
    marginRight: "16px",
    height: "104px",
    width: "104px",

    [`@media (min-width: ${mq.min["768"]})`]: {
      marginRight: "24px",
      height: "120px",
      width: "120px",
    },

    [`@media (max-width: ${mq.max["1200"]})`]: {
      alignSelf: "flex-start",
    },

    [`@media (min-width: ${mq.min["1200"]})`]: {
      marginRight: "28px",
      height: "189px",
      width: "189px",
    },
  },

  image: {
    borderRadius: "8px",
    display: "block",
  },

  info: {
    flex: "1 1 auto",

    [`@media (min-width: ${mq.min["1200"]})`]: {
      paddingBottom: "16px",
      paddingTop: "16px",
    },
  },

  label: {
    marginBottom: "2px",

    [`@media (min-width: ${mq.min["1200"]})`]: {
      marginBottom: "8px",
    },
  },

  topChoice: {
    color: colors.accentRed,
  },

  title: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      fontSize: `${fontSizeHeading7}px`,
      lineHeight: lineHeightHeading7,
    },

    [`@media (max-width: ${mq.max["1200"]})`]: {
      fontSize: `${fontSizeHeading6}px`,
      lineHeight: lineHeightHeading6,
    },
  },

  location: {
    marginTop: "8px",

    [`@media (max-width: ${mq.max["1200"]})`]: {
      display: "none",
    },
  },

  perksList: {
    listStyle: "none",
    lineHeight: lineHeightReset,
    marginTop: "2px",

    [`@media (min-width: ${mq.min["1200"]})`]: {
      marginTop: "8px",
    },
  },

  perksListItem: {
    display: "inline-block",
  },

  perksListItemTablet: {
    [`@media (max-width: ${mq.max["1200"]})`]: {
      display: "none",
    },
  },

  perksListItemMobile: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      display: "none",
    },
  },

  perksListItemText: {
    [`@media (max-width: ${mq.max["1200"]})`]: {
      fontSize: `${fontSizeUppercase}px`,
      lineHeight: (16 / fontSizeUppercase),
    },
  },

  reviewed: {
    color: colors.textPrimary,
    marginTop: "32px",

    [`@media (max-width: ${mq.max["1200"]})`]: {
      fontSize: "12px",
      marginTop: "2px",
    },
  },

  booking: {
    color: colors.textPrimary,
    flex: "0 0 125px",
    marginLeft: "56px",
    maxWidth: "125px",
    textAlign: "center",

    [`@media (max-width: ${mq.max["1200"]})`]: {
      display: "none",
    },
  },

  price: {
    fontSize: `${fontSizeHeading3}px`,
    fontWeight: fontWeightLight,
    lineHeight: lineHeightHeading3,
    marginBottom: "4px",
  },

  priceSmall: {
    color: colors.textPrimary,
    fontSize: `${fontSizeHeading7}px`,
    fontWeight: fontWeightMedium,
    lineHeight: lineHeightHeading7,
    marginBottom: "2px",
    marginTop: "2px",

    [`@media (min-width: ${mq.min["768"]}) and (max-width: ${mq.max["1200"]})`]: {
      fontSize: `${fontSizeHeading6}px`,
      lineHeight: lineHeightHeading6,
    },

    [`@media (min-width: ${mq.min["1200"]})`]: {
      display: "none",
    },
  },

  currency: {
    fontSize: `${fontSizeHeading5}px`,
    lineHeight: lineHeightHeading5,
  },

  priceOld: {
    color: colors.accentRed,
    fontSize: `${fontSizeBodySmall}px`,
    lineHeight: lineHeightBodySmall,
    marginBottom: "4px",
  },

  callToActionMobile: {
    [`@media (max-width: ${mq.max["1200"]})`]: {
      display: "inline-flex",
      flexDirection: "column-reverse",
      textAlign: "left",
    },

    [`@media (min-width: ${mq.min["1200"]})`]: {
      display: "none",
    },
  },

  callToActionMobileText: {
    [`@media (max-width: ${mq.max["1200"]})`]: {
      marginBottom: "8px",
      marginTop: "12px",
      paddingLeft: 0,
      paddingRight: 0,
    },
  },

  rating: {
    fontFamily: font("miller"),
    fontSize: `${fontSizeUppercase}px`,
    fontStyle: "italic",
    lineHeight: (16 / fontSizeUppercase),
  },

  provider: {
    fontSize: `${fontSizeUppercase}px`,
    fontWeight: fontWeightMedium,
    lineHeight: (16 / fontSizeUppercase),
    marginTop: "4px",
  },

  button: {
    alignItems: "center",
    backgroundColor: colors.linkPrimary,
    borderRadius: "22px",
    color: colors.textOverlay,
    display: "flex",
    fontSize: `${fontSizeUppercase}px`,
    fontWeight: fontWeightMedium,
    height: "44px",
    justifyContent: "center",
    lineHeight: lineHeightReset,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: "32px",
    textAlign: "center",
    textTransform: "uppercase",
    width: "100%",
  },
};

const returnCityCenter = (place, location) => {
  let cityCenter;

  if (!place.center) {
    return "";
  }

  const cityFromAncestry = Array.isArray(place.ancestry) &&
    place.ancestry.filter(
      containingPlace => containingPlace.placeType === "city"
    );

  if (place.placeType === "city") {
    cityCenter = place.center.coordinates;
  } else if (cityFromAncestry.length) {
    cityCenter = cityFromAncestry[0].center.coordinates;
  } else {
    cityCenter = place.center.coordinates;
  }

  if (isEmpty(location) || !cityCenter) {
    return "";
  }

  const distanceToCityCenter = distanceTo(location.coordinates, cityCenter);
  return `${distanceToCityCenter[distanceUnits()].amount}
    ${distanceToCityCenter[distanceUnits()].unit} from city center`;
};

/*
 * Combine styles so the 3rd & 4th perks are hidden on tablet/mobile screens
 */
const getPerksListItemStyle = (index) => {
  if (index >= 3) {
    return [styles.perksListItem, styles.perksListItemTablet];
  } else if (index >= 2) {
    return [styles.perksListItem, styles.perksListItemMobile];
  }

  return styles.perksListItem;
};

const ListItemHotel = ({
  title,
  slug,
  image,
  place,
  onShowPrices,
  providers,
  price,
  hidePrice,
  location,
  reviewed,
  perks,
  topChoice,
  subtype,
  soldOut,
  id,
}) => {
  const provider = providers && providers.length > 0 ?
    hotelProvider(providers) : {};

  const subtypeMap = {
    "b&b": "bed and breakfast",
    "boutique hotel": "hotel",
    "historic hotel": "hotel",
    "5 star hotel": "hotel",
    "4 star hotel": "hotel",
    "3 star hotel": "hotel",
    "2 star hotel": "hotel",
  };

  const subtypeLabel = subtypeMap[subtype] || subtype || "hotel";

  return (
    <a href={slug} style={styles.anchor}>
      <div id={id} className="ListItem-hotel" style={styles.container}>
        <div style={styles.imageContainer}>
          <ListItemImage
            style={styles.image}
            title={title}
            image={{ path: imgix(image.path, {}, "bdc") }}
            aspectRatio={1}
          />
        </div>

        <div style={styles.info}>
          {topChoice &&
            <TextUppercase style={[styles.label, styles.topChoice]}>
              Top choice {subtypeLabel}
            </TextUppercase>
          }

          {!topChoice &&
            <TextUppercase style={styles.label}>
              {subtypeLabel}
            </TextUppercase>
          }

          <Heading
            size={5}
            weight="medium"
            style={styles.title}
          >
            {title}
          </Heading>

          <TextBodySmall style={styles.location}>
            Located in {place.name}{place && !isEmpty(location) && ` — ${returnCityCenter(place, location)}`}
          </TextBodySmall>

          {perks.length > 0 &&
            <ul style={styles.perksList}>
              {perks.map((item, index) => (
                <li style={getPerksListItemStyle(index)} key={item}>
                  <TextBodySmall style={styles.perksListItemText}>
                    {index !== 0 &&
                      <Bullet space="both" color="#d8d8d8" />
                    }
                    {_.upperFirst(item)}
                  </TextBodySmall>
                </li>
              ))}
            </ul>
          }

          {price && price.amount &&
            <div style={styles.priceSmall}>
              {currencySymbol[price.currency || "USD"]}{price.amount}
            </div>
          }

          {reviewed &&
            <ReviewedBadge style={styles.reviewed} />
          }

          {!hidePrice && soldOut &&
            <ListItemHotelCallToAction
              style={styles.callToActionMobile}
              styleText={styles.callToActionMobileText}
              type="soldOut"
              onClick={(event) => {
                event.preventDefault();
                onShowPrices();
              }}
            />
          }

          {hidePrice &&
            <ListItemHotelCallToAction
              style={styles.callToActionMobile}
              styleText={styles.callToActionMobileText}
              type="noDates"
              onClick={(event) => {
                event.preventDefault();
                onShowPrices();
              }}
            />
          }
        </div>

        <div style={styles.booking}>
          {price && price.old &&
            <div style={styles.priceOld} title="Old price">
              <s>{currencySymbol[price.old.currency || "USD"]} {price.old.amount}</s>
            </div>
          }

          {!hidePrice && price && price.amount &&
            <div style={styles.price}>
              <sup style={styles.currency}>
                {currencySymbol[price.currency || "USD"]}
              </sup>
              {price.amount}
            </div>
          }

          {!hidePrice && soldOut &&
            <ListItemHotelCallToAction
              type="soldOut"
              onClick={(event) => {
                event.preventDefault();
                onShowPrices();
              }}
            />
          }

          {hidePrice &&
            <ListItemHotelCallToAction
              type="noDates"
              onClick={(event) => {
                event.preventDefault();
                onShowPrices();
              }}
            />
          }

          {provider.data && provider.data.score &&
            <div style={styles.rating}>
              {ratingCopy(provider.type, provider.data.score)}
            </div>
          }

          <div style={styles.provider}>
            {provider.name}
          </div>

          <div style={styles.button}>
            View
          </div>
        </div>
      </div>
    </a>
  );
};

ListItemHotel.propTypes = {
  title: PropTypes.string,
  slug: PropTypes.string,
  place: PropTypes.object,
  onShowPrices: PropTypes.func,
  providers: PropTypes.array,
  price: PropTypes.object,
  hidePrice: PropTypes.bool,
  location: PropTypes.object,
  reviewed: PropTypes.bool,
  perks: PropTypes.array,
  image: PropTypes.object,
  topChoice: PropTypes.bool,
  subtype: PropTypes.string,
  soldOut: PropTypes.bool,
  id: PropTypes.string,
};

ListItemHotel.defaultProps = {
  title: "",
  slug: "",
  place: {},
  onShowPrices: null,
  providers: [],
  price: {},
  hidePrice: false,
  location: {},
  reviewed: false,
  perks: [],
  image: {},
  topChoice: false,
  subtype: "",
  soldOut: false,
  id: null,
};

export default radium(ListItemHotel);



// WEBPACK FOOTER //
// ./assets/pois/components/listItemHotel/index.jsx